// Sawyer 09 pet-inactivity (D1)
/**
 * Settings → Capacity & assessments: the pet inactivity rule.
 *
 * Sawyer (memo 1, 2026-10-23): "it'd be nice if our pets expire after 6
 * months of inactivity ... just the dog, but keep the user profile active".
 * One number, owner and manager, off until set. Nothing is stored per dog: the
 * server derives "inactive" from the last checked-out visit on every read.
 *
 * Design Philosophy principles served:
 *  - #1 One glance, one truth: the status sentence is built from the
 *    server's answer, not from the input, so it always states the rule in
 *    force.
 *  - #2 The next action is obvious: one primary Save, enabled only when the
 *    value is valid and differs from what the server holds; "Turn off" is a
 *    secondary action shown only when a rule is set.
 *  - #5 Never lie about data: "Not set: off" is labelled as the default, and
 *    when no assessment schedule exists the card says the bookings are NOT
 *    held rather than implying they are.
 *  - #7 Built for hands: 44px controls; the input and buttons stack at 375.
 *  - Five states: loading · error(+retry) · off · configured · saving
 *    (+ save error, validation, read-only).
 *
 * Deploy window (refuter R6): frontends auto-deploy, the server does not.
 * An older server strips `petInactivityMonths` from the PUT and answers 400,
 * so the card renders NOTHING unless the GET carries
 * `source.petInactivityMonths` (present only on a server that can save it).
 *
 * Counter Test: Ted types 6 on his phone and taps Save; the card reads "Pets
 * become inactive after 6 months without a checked-out visit", and Biscuit,
 * last here in December, drops off the vaccination list on the next load.
 *
 * Server contract: GET/PUT /api/v2/admin/tenant/operations (PATCH semantics:
 * this card sends only its own key) + GET /api/v2/admin/tenant/assessment-schedule.
 */
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Alert, Button, Card, Input, Skeleton } from '@ferro/ui';
import {
  adminTenantCapacityApi,
  adminTenantSettingsApi,
  type TenantOperationsSettings,
} from '../../lib/api.js';
import { OPERATIONS_KEY_LABELS } from './OperationsSettingsCard.js';

interface PetInactivityCardProps {
  apiUrl: string;
  /** Holds business-config:write:all (owner, manager). Everyone else reads. */
  canEdit: boolean;
}

export const PET_INACTIVITY_MONTHS_MESSAGE = 'Enter a whole number of months from 1 to 36';
const MIN_MONTHS = 1;
const MAX_MONTHS = 36;

/** The input as a whole number of months in range, or null when it is not one. */
export function parseMonthsInput(raw: string, min = MIN_MONTHS, max = MAX_MONTHS): number | null {
  const t = raw.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return Number.isInteger(n) && n >= min && n <= max ? n : null;
}

/** The sentence that states the rule in force. */
export function petInactivityStatusLine(months: number | null): string {
  if (months === null) return 'Off. No pet becomes inactive.';
  return `Pets become inactive after ${months} ${months === 1 ? 'month' : 'months'} without a checked-out visit.`;
}

function whenLabel(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }).format(d);
}

type Status = 'loading' | 'error' | 'ready' | 'unsupported';

export function PetInactivityCard({ apiUrl, canEdit }: PetInactivityCardProps): JSX.Element | null {
  const [status, setStatus] = useState<Status>('loading');
  const [settings, setSettings] = useState<TenantOperationsSettings | null>(null);
  const [assessmentConfigured, setAssessmentConfigured] = useState<boolean | null>(null);
  const [draft, setDraft] = useState('');
  const [touched, setTouched] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  const apply = useCallback((s: TenantOperationsSettings) => {
    setSettings(s);
    const m = s.operations.petInactivityMonths ?? null;
    setDraft(m === null ? '' : String(m));
    setTouched(false);
  }, []);

  const load = useCallback(async () => {
    setStatus('loading');
    setSaveError(null);
    const [ops, schedule] = await Promise.all([
      adminTenantSettingsApi.getOperations(apiUrl),
      adminTenantCapacityApi.getAssessmentSchedule(apiUrl),
    ]);
    if (ops.error || !ops.data) {
      setStatus('error');
      return;
    }
    if (ops.data.source.petInactivityMonths === undefined) {
      setStatus('unsupported');
      return;
    }
    apply(ops.data);
    // The schedule read is decoration: unknown means "no warning", never a block.
    setAssessmentConfigured(schedule.data ? schedule.data.schedule.configured : null);
    setStatus('ready');
  }, [apiUrl, apply]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = useCallback(
    async (next: number | null) => {
      setSaveError(null);
      setIsSaving(true);
      const res = await adminTenantSettingsApi.updateOperations(apiUrl, { petInactivityMonths: next });
      setIsSaving(false);
      if (res.error || !res.data) {
        setSaveError(res.error ?? 'Please try again.');
        return;
      }
      apply(res.data);
    },
    [apiUrl, apply],
  );

  if (status === 'unsupported') return null;

  if (status === 'loading') {
    return (
      <Card title="Pet inactivity">
        <div data-testid="pet-inactivity-loading">
          <Skeleton variant="text" lines={3} />
        </div>
      </Card>
    );
  }

  if (status === 'error' || !settings) {
    return (
      <Card title="Pet inactivity">
        <div className="ferro-settings__card" data-testid="pet-inactivity-error">
          <Alert variant="error">Couldn't load the pet inactivity rule.</Alert>
          <div className="ferro-settings__actions">
            <Button variant="outline" onClick={() => void load()} data-testid="pet-inactivity-retry">
              Retry
            </Button>
          </div>
        </div>
      </Card>
    );
  }

  const months = settings.operations.petInactivityMonths ?? null;
  const configured = settings.source.petInactivityMonths === 'configured' && months !== null;
  const min = settings.limits.minPetInactivityMonths ?? MIN_MONTHS;
  const max = settings.limits.maxPetInactivityMonths ?? MAX_MONTHS;
  const parsed = parseMonthsInput(draft, min, max);
  const invalid = draft.trim() !== '' && parsed === null;
  const canSave = parsed !== null && parsed !== months && !isSaving;
  const lastChange = settings.lastChange;
  const lastChangeNamesThis = lastChange?.changedKeys.includes('petInactivityMonths') ?? false;

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (canSave && parsed !== null) void save(parsed);
  };

  return (
    <Card title="Pet inactivity" subtitle={configured ? `${months} months` : 'Not set: off'}>
      <div className="ferro-settings__card" data-testid="pet-inactivity-card">
        <p className="ferro-settings__teaching">
          A pet with no checked-out visit for this many months is marked inactive. Inactive pets drop off the
          vaccination compliance lists, show &lsquo;Inactive since&rsquo; on their profile, and need a new meet and
          greet before their next daycare or boarding booking. The owner&rsquo;s account stays active.
        </p>
        <p className="ferro-settings__preview" data-testid="pet-inactivity-status" aria-live="polite">
          {petInactivityStatusLine(configured ? months : null)}
        </p>
        {configured && assessmentConfigured === false ? (
          <Alert variant="warning">
            <span data-testid="pet-inactivity-no-assessment">
              Meet and greet scheduling is off, so inactive pets drop off the vaccination lists but their bookings
              are not held. Set up the assessment schedule above to hold them.
            </span>
          </Alert>
        ) : null}

        {canEdit ? (
          <form className="ferro-form ferro-settings__form" onSubmit={onSubmit} noValidate>
            <Input
              name="petInactivityMonths"
              label="Months without a visit"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              value={draft}
              disabled={isSaving}
              onChange={(e) => {
                setDraft(e.target.value);
                setTouched(true);
                setSaveError(null);
              }}
              error={invalid && touched ? PET_INACTIVITY_MONTHS_MESSAGE : undefined}
              helperText={invalid && touched ? undefined : `Whole months, ${min} to ${max}.`}
              data-testid="pet-inactivity-input"
            />
            {saveError ? (
              <Alert variant="error">
                <span data-testid="pet-inactivity-save-error">Couldn't save. Nothing was changed. {saveError}</span>
              </Alert>
            ) : null}
            <div className="ferro-settings__actions">
              <Button type="submit" variant="primary" isLoading={isSaving} disabled={!canSave} data-testid="pet-inactivity-save">
                Save
              </Button>
              {configured ? (
                <Button
                  type="button"
                  variant="ghost"
                  disabled={isSaving}
                  onClick={() => void save(null)}
                  data-testid="pet-inactivity-off"
                >
                  Turn off
                </Button>
              ) : null}
            </div>
          </form>
        ) : (
          <p className="ferro-muted" data-testid="pet-inactivity-readonly">
            Only an owner or manager can change this.
          </p>
        )}

        {lastChange && lastChangeNamesThis ? (
          <p className="ferro-settings__last-change" data-testid="pet-inactivity-last-change">
            Last changed by <strong>{lastChange.byName}</strong>
            {lastChange.at ? ` on ${whenLabel(lastChange.at)}` : ''} — {OPERATIONS_KEY_LABELS.petInactivityMonths}
          </p>
        ) : null}
      </div>
    </Card>
  );
}
